/**
 * @fileoverview Contexto de React para el manejo del estado del Panel Dinámico.
 *
 * Este módulo proporciona el contexto, reducer y hooks personalizados para gestionar
 * el estado global del Panel Dinámico, incluyendo el layout activo, modo de edición,
 * filtros de período y objetivos globales.
 *
 * @module DynamicViewContext
 * @requires React
 * @requires getDefaultPeriod - Utilidad para obtener el período por defecto desde localStorage.
 *
 * @since 1.0.0
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */

import React from 'react';
import { getDefaultPeriod } from '../../../utils/local-storage/select-periodo';

/**
 * Rango de fechas por defecto para el filtro de período.
 *
 * Obtiene el período guardado en localStorage o utiliza un valor por defecto
 * de "Última hora".
 *
 * @constant {Object} defaultSelectDateRange
 * @property {string} value - Identificador del período ('hora').
 * @property {string} text - Texto descriptivo del período ('Última hora').
 * @property {number} typePeriod - Tipo de período (0).
 * @property {string} period - Abreviación del período ('h').
 */
export const defaultSelectDateRange = getDefaultPeriod()
  ?.parseSelectedGlobalPeriod ?? {
  value: 'hora',
  text: 'Última hora',
  typePeriod: 0,
  period: 'h',
};

/**
 * Tipos de acciones disponibles para el reducer del Panel Dinámico.
 *
 * @constant {Object} DYNAMIC_VIEW_REDUCER_ACTIONS
 * @property {string} SET_ACTIVE_LAYOUT - Establece el layout activo completo.
 * @property {string} SET_IS_EDITING - Activa/desactiva el modo de edición.
 * @property {string} SET_FILTER_PERIOD - Actualiza el filtro de período.
 * @property {string} SET_GLOBAL_OBJECTIVE - Establece el objetivo global.
 * @property {string} SET_ACTIVE_LAYOUT_ID - Establece el ID del layout activo.
 * @property {string} SET_IS_EDITING_TITLE - Activa/desactiva la edición del título.
 * @property {string} ADD_NEW_CARD - Añade una nueva tarjeta al layout.
 * @property {string} UPDATE_TEMPLATE - Actualiza la plantilla del layout.
 * @property {string} SET_REFRESH_TIME - Establece el tiempo de refresco automático.
 */
const DYNAMIC_VIEW_REDUCER_ACTIONS = {
  SET_ACTIVE_LAYOUT: 'SET_ACTIVE_LAYOUT',
  SET_IS_EDITING: 'SET_IS_EDITING',
  SET_FILTER_PERIOD: 'SET_FILTER_PERIOD',
  SET_GLOBAL_OBJECTIVE: 'SET_GLOBAL_OBJECTIVE',
  SET_ACTIVE_LAYOUT_ID: 'SET_ACTIVE_LAYOUT_ID',
  SET_IS_EDITING_TITLE: 'SET_IS_EDITING_TITLE',
  ADD_NEW_CARD: 'ADD_NEW_CARD',
  UPDATE_TEMPLATE: 'UPDATE_TEMPLATE',
  SET_REFRESH_TIME: 'SET_REFRESH_TIME',
};

/**
 * Estado inicial del contexto del Panel Dinámico.
 *
 * @constant {Object} initialState
 * @property {Array} activeLayout - Array de tarjetas/widgets en el layout activo.
 * @property {boolean} isEditing - Indica si el panel está en modo edición.
 * @property {Object} filterPeriod - Configuración del filtro de período de tiempo.
 * @property {Object} globalObjective - Configuración del objetivo global.
 * @property {number|null} globalObjective.objetivoId - ID del objetivo seleccionado.
 * @property {boolean} globalObjective.applyToAll - Si se aplica a todos los widgets.
 * @property {number|null} activeLayoutId - ID del layout actualmente activo.
 * @property {boolean} isEditingTitle - Indica si se está editando el título.
 * @property {number} refreshTime - Intervalo de refresco automático en milisegundos.
 */
export const initialState = {
  activeLayout: [],
  isEditing: false,
  filterPeriod: defaultSelectDateRange,
  globalObjective: {
    objetivoId: null,
    applyToAll: false,
  },
  activeLayoutId: null,
  isEditingTitle: false,
  refreshTime: 0,
};

/**
 * Reducer para gestionar el estado del Panel Dinámico.
 *
 * Maneja todas las acciones relacionadas con el layout, edición, filtros
 * y configuración del panel dinámico.
 *
 * @function dynamicViewReducer
 * @param {Object} state - Estado actual del contexto.
 * @param {Object} action - Acción a ejecutar.
 * @param {string} action.type - Tipo de acción (ver DYNAMIC_VIEW_REDUCER_ACTIONS).
 * @param {*} action.payload - Datos asociados a la acción.
 * @returns {Object} Nuevo estado actualizado.
 *
 * @example
 * // Establecer modo de edición
 * dispatch({ type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_IS_EDITING, payload: true });
 *
 * @example
 * // Añadir una nueva tarjeta
 * dispatch({ type: DYNAMIC_VIEW_REDUCER_ACTIONS.ADD_NEW_CARD, payload: newCard });
 */
export const dynamicViewReducer = (state = initialState, action) => {
  switch (action.type) {
    case DYNAMIC_VIEW_REDUCER_ACTIONS.SET_ACTIVE_LAYOUT:
      return {
        ...state,
        activeLayout: action.payload,
      };
    case DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE:
      return {
        ...state,
        activeLayout: action.payload,
      };
    case DYNAMIC_VIEW_REDUCER_ACTIONS.SET_IS_EDITING:
      return {
        ...state,
        isEditing: action.payload,
      };
    case DYNAMIC_VIEW_REDUCER_ACTIONS.SET_FILTER_PERIOD:
      return {
        ...state,
        filterPeriod: action.payload,
      };
    case DYNAMIC_VIEW_REDUCER_ACTIONS.SET_GLOBAL_OBJECTIVE:
      return {
        ...state,
        globalObjective: {
          ...state.globalObjective,
          ...action.payload,
        },
      };
    case DYNAMIC_VIEW_REDUCER_ACTIONS.SET_ACTIVE_LAYOUT_ID:
      return {
        ...state,
        activeLayoutId: action.payload,
      };
    case DYNAMIC_VIEW_REDUCER_ACTIONS.SET_IS_EDITING_TITLE:
      return {
        ...state,
        isEditingTitle: action.payload,
      };
    case DYNAMIC_VIEW_REDUCER_ACTIONS.ADD_NEW_CARD:
      return {
        ...state,
        activeLayout: [...state.activeLayout, action.payload],
      };
    case DYNAMIC_VIEW_REDUCER_ACTIONS.SET_REFRESH_TIME:
      return {
        ...state,
        refreshTime: action.payload,
      };
    default:
      return state;
  }
};

/**
 * Contexto de React para el estado del Panel Dinámico.
 *
 * Proporciona acceso de solo lectura al estado actual del panel.
 *
 * @constant {React.Context<Object|null>} DynamicViewStateContext
 */
const DynamicViewStateContext = React.createContext(null);

/**
 * Contexto de React para el dispatch del Panel Dinámico.
 *
 * Proporciona acceso a la función dispatch para actualizar el estado.
 *
 * @constant {React.Context<Function|null>} DynamicViewDispatchContext
 */
const DynamicViewDispatchContext = React.createContext(null);

/**
 * Hook personalizado para acceder al estado del Panel Dinámico.
 *
 * @function useDynamicViewState
 * @returns {Object} Estado actual del contexto del Panel Dinámico.
 * @throws {Error} Si se usa fuera de un DynamicViewStateContext.Provider.
 *
 * @example
 * const { activeLayout, isEditing, filterPeriod } = useDynamicViewState();
 */
const useDynamicViewState = () => {
  const context = React.useContext(DynamicViewStateContext);
  if (!context) {
    throw new Error(
      'useDynamicViewState must be used within a DynamicViewStateContext.Provider'
    );
  }
  return context;
};

/**
 * Hook personalizado para acceder al dispatch del Panel Dinámico.
 *
 * @function useDynamicViewDispatch
 * @returns {Function} Función dispatch para actualizar el estado.
 * @throws {Error} Si se usa fuera de un DynamicViewDispatchContext.Provider.
 *
 * @example
 * const dispatch = useDynamicViewDispatch();
 * dispatch({ type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_IS_EDITING, payload: true });
 */
const useDynamicViewDispatch = () => {
  const context = React.useContext(DynamicViewDispatchContext);
  if (!context) {
    throw new Error(
      'useDynamicViewDispatch must be used within a DynamicViewDispatchContext.Provider'
    );
  }
  return context;
};

/**
 * Hook personalizado que combina estado y dispatch del Panel Dinámico.
 *
 * Proporciona una forma conveniente de acceder tanto al estado como al
 * dispatch en un solo hook.
 *
 * @function useDynamicViewContext
 * @returns {Object} Objeto con state y dispatch.
 * @returns {Object} return.state - Estado actual del Panel Dinámico.
 * @returns {Function} return.dispatch - Función dispatch para actualizar el estado.
 *
 * @example
 * const { state, dispatch } = useDynamicViewContext();
 *
 * // Acceder al estado
 * console.log(state.activeLayout);
 *
 * // Despachar una acción
 * dispatch({ type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_IS_EDITING, payload: false });
 */
function useDynamicViewContext() {
  const state = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();
  return { state, dispatch };
}

export {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  DynamicViewStateContext,
  DynamicViewDispatchContext,
  useDynamicViewState,
  useDynamicViewDispatch,
  useDynamicViewContext,
};
